import { Skeleton } from '@/shared/components/ui/skeleton';
import { ErrorState } from '../../analytics/shared/ErrorState';
import { useReferralFeedbackCsDetail } from '../../../hooks/useReferralFeedbackData';
import type {
  ReferralCsSubjectType,
  ReferralFeedbackCsItem,
} from '../../../types/engagement-metrics.types';

const SOURCE_BADGE: Record<ReferralCsSubjectType, { label: string; text: string; bg: string }> = {
  MESSAGE: { label: 'First message', text: '#2c6e7d', bg: '#e2edf0' },
  CONSULTATION_REQUEST: { label: 'Requested a call', text: '#46584f', bg: '#edeae3' },
};

const dash = '—';
const yesNo = (value: boolean | null): string => (value == null ? dash : value ? 'Yes' : 'No');
const ratingText = (value: number | null): string => (value != null ? `${value}/5` : dash);

const SourceBadge = ({ subjectType }: { subjectType: ReferralCsSubjectType }) => {
  const badge = SOURCE_BADGE[subjectType] ?? { label: subjectType, text: '#7f8a83', bg: '#f0ede6' };
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[.3px]"
      style={{ color: badge.text, backgroundColor: badge.bg }}
    >
      {badge.label}
    </span>
  );
};

const AnswerPair = ({ label, value }: { label: string; value: string }) => (
  <span className="text-[12px]" style={{ color: '#46584f' }}>
    <span className="font-bold uppercase text-[10.5px] tracking-[.3px]" style={{ color: '#7f8a83' }}>
      {label}
    </span>{' '}
    {value}
  </span>
);

const CsItemRow = ({ item }: { item: ReferralFeedbackCsItem }) => (
  <li
    className="border-b last:border-0 py-3 first:pt-1 last:pb-1 space-y-1.5"
    style={{ borderColor: '#edeae3' }}
    data-testid="referral-cs-detail-row"
  >
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <SourceBadge subjectType={item.subjectType} />
      <span className="text-[12.5px] font-semibold" style={{ color: '#1b2a23' }}>
        {new Date(item.respondedAt).toLocaleDateString()}
      </span>
      <span className="text-[12.5px]" style={{ color: '#46584f' }}>
        {item.careSeekerName ?? dash}
      </span>
    </div>
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      <AnswerPair label="Connected" value={yesNo(item.csReached)} />
      <AnswerPair label="Rating" value={ratingText(item.csRating)} />
    </div>
  </li>
);

/**
 * Inline drill-down inside an expanded CS-table provider row: each individual
 * care-seeker answer for that SP (first messages + call requests). CS answers
 * only — SP follow-up data lives in the SP table. Mounted only on expand, so
 * the query fires lazily.
 */
export const ReferralFeedbackCsDetailPanel = ({ spUserId }: { spUserId: string }) => {
  const { data, isLoading, isError, refetch } = useReferralFeedbackCsDetail(spUserId);

  if (isError) {
    return (
      <ErrorState
        message="Failed to load care-seeker feedback for this provider. Please try again."
        onRetry={() => refetch()}
      />
    );
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-2 py-2" data-testid="referral-cs-detail-loading">
        <Skeleton className="h-14 w-full rounded-lg" />
        <Skeleton className="h-14 w-full rounded-lg" />
      </div>
    );
  }

  if (data.items.length === 0) {
    return (
      <p className="py-2 text-sm text-gray-500">No care-seeker feedback for this provider yet.</p>
    );
  }

  return (
    <ul>
      {data.items.map((item) => (
        <CsItemRow key={`${item.subjectType}-${item.subjectId}`} item={item} />
      ))}
    </ul>
  );
};
